import React, { Suspense, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, useTheme, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import DeviceGroupErrorBoundary from './components/DeviceGroupErrorBoundary'
import { getWidgetColors } from '../SingleDeviceSelector/utils/helpers'
import { normalizeHiddenDeviceIds } from '../../common/deviceVisibility/hiddenDeviceIds'

// Lazy load the content component for performance optimization
const DeviceGridContent = React.lazy(() => import('./components/DeviceGridContent'))

/**
 * Main widget component for displaying a group of devices.
 * Implements Error Boundary, Suspense for lazy loading, and modular architecture.
 *
 * @param {Object} props
 * @param {number} props.w - Widget width (gridstack).
 * @param {Object} props.data - Widget configuration data.
 * @param {Function} props.navigateToDeviceId - Navigation handler.
 */
const DeviceGroupWidget = ({ w, data, navigateToDeviceId }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const colors = useMemo(() => getWidgetColors(theme), [theme])

  const deviceGroupId = data?.deviceGroupId
  const includeFoJobWorkers = data?.includeFoJobWorkers ?? false
  const showPanel = data?.showPanel ?? true
  const initialGroupName = data?.groupName

  // Normalised once and memoised on the stored value: the grid polls once a second, and handing it
  // a new array on every render would rebuild the collection far more often than the data changes.
  const hiddenDeviceIds = useMemo(() => normalizeHiddenDeviceIds(data?.hiddenDeviceIds), [data?.hiddenDeviceIds])

  // Handle missing configuration
  if (!deviceGroupId) {
    return (
      <Box
        sx={{
          p: 2,
          color: colors.textLight,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >
        <Typography>
          {t_('Please set a device group id in configuration')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        color: colors.textLight,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <DeviceGroupErrorBoundary t_={t_}>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <CircularProgress color='secondary' />
              </Box>
            }
          >
            <DeviceGridContent
              deviceGroupId={deviceGroupId}
              includeFoJobWorkers={includeFoJobWorkers}
              showPanel={showPanel}
              navigateToDeviceId={navigateToDeviceId}
              initialGroupName={initialGroupName}
              hiddenDeviceIds={hiddenDeviceIds}
            />
          </Suspense>
        </Box>
      </DeviceGroupErrorBoundary>
    </Box>
  )
}

DeviceGroupWidget.propTypes = {
  w: PropTypes.number,
  data: PropTypes.shape({
    deviceGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    includeFoJobWorkers: PropTypes.bool,
    showPanel: PropTypes.bool,
    groupName: PropTypes.string,
    hiddenDeviceIds: PropTypes.any
  }),
  navigateToDeviceId: PropTypes.func
}

export default DeviceGroupWidget
